import React, { useState, useEffect } from 'react';
import Calendar from './Calendar';
import LoginPage from './LoginPage';
import { addMonths } from 'date-fns';
import './App.css';
import './LoginPage.css';
import AddEventPopupModal from './AddEventPopupModal';
import { auth } from "./Firebase";
import { onAuthStateChanged, signOut, User as FirebaseUser } from "firebase/auth";
import NextTrack from './NextTrack';

interface Event {
  track_id: string;
  timestamp: number;
  listen_count?: number;
  duration_seconds?: number;
}

interface EventsByDate {
  [date: string]: Event[];
}

function App() {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [inputUserId, setInputUserId] = useState('');
  const [activeUserId, setActiveUserId] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [events, setEvents] = useState<EventsByDate>({});

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      setUser(user);
    });
  }, []);

  const handleLogout = () => {
    signOut(auth).catch(console.error);
  };

  if (!user) {
    return <LoginPage />;
  }

  const goToNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(addMonths(currentMonth, -1));
  };

  const handleDateClick = async (date: Date) => {
    setSelectedDate(date);
  };

  const handleSubmitProgress = async ({ date, track_id }: { date: Date; track_id: string }) => {
    try {
      const utcMidnight = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
      const timestamp = utcMidnight;
      const token = await user.getIdToken();
      const authHeader = `Bearer ${token}`;
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/progress-events`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": authHeader },
        body: JSON.stringify({ events: [{
          user_id: activeUserId,
          track_id,
          timestamp,
        }] }),
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const dateStr = new Date(timestamp).toISOString().slice(0, 10);
      setEvents(prevEvents => {
        const prevDayEvents = Array.isArray(prevEvents[dateStr]) ? prevEvents[dateStr] : [];
        const newEvent: Event = { track_id, timestamp };
        return {
          ...prevEvents,
          [dateStr]: [...prevDayEvents, newEvent]
        };
      });
    } catch (err) {
      console.error(err);
      alert("Error creating progress");
    }
  };

  const applyUserId = () => {
    setActiveUserId(inputUserId);
    setRefreshKey(prev => prev + 1);
  };

  const isValidUUID = (str: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  return (
    <div className="app-container">
      <p style={{ textAlign: 'right', padding: '10px' }}>
        Welcome {user.email}
        <button className="signout-button" onClick={handleLogout} style={{ marginLeft: '10px' }}>
          <img
            src="https://img.icons8.com/ios-glyphs/30/000000/logout-rounded--v1.png"
            alt="Logout icon"
            style={{ width: 18, height: 18 }}
          />
          Sign Out
        </button>
      </p>
      <div className="user-input">
        <input
          type="text"
          value={inputUserId}
          onChange={(e) => setInputUserId(e.target.value)}
          placeholder="Enter User ID"
        />
        <button onClick={applyUserId} disabled={!isValidUUID(inputUserId)} className="go-button">Go</button>
      </div>
      <div className='user-input-error'>
          {inputUserId && !isValidUUID(inputUserId) && (
          <div style={{ marginTop: '8px', color: 'red', fontSize: '0.9em' }}>
            Please enter a valid UUID (e.g. b6fdac96-8ac9-437a-9f44-54e8c03beeb1)
          </div>
        )}
      </div>
      <div className="nav-bar">
        <button onClick={goToPreviousMonth} className="nav-button">← Prev</button>
        <h2>{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</h2>
        <button onClick={goToNextMonth} className="nav-button">Next →</button>
      </div>
      <NextTrack userId={activeUserId} user={user} />
      <Calendar
        selectedMonth={currentMonth}
        userId={activeUserId}
        onDateClick={handleDateClick}
        user={user}
        refreshKey={refreshKey}
        events={events}
        setEvents={setEvents}
      />
      {selectedDate && (
        <AddEventPopupModal
          date={selectedDate}
          onClose={() => setSelectedDate(null)}
          onSubmit={handleSubmitProgress}
        />
      )}
    </div>
  );
}

export default App;
